// Proje Detay — "Müşteri Katılımcıları" kartı. Müşteri Katılımcı Rolü plan
// (docs/superpowers/plans/2026-07-23-musteri-katilimci-rolu.md) Task 7.
// İzin-gated: projects.invite_client yoksa hiç render edilmez (ProjectAuditWidget
// pattern'iyle aynı). Liste + davet modalı + satır aksiyonları (yeniden aç/iptal/resend).

import { useEffect, useState } from "react";
import type { ParticipantStatus, ProjectParticipant } from "@mahir/shared";
import { ApiError } from "../../api/client";
import {
  listParticipants,
  resendParticipantInvite,
  setParticipantStatus,
} from "../../api/participants";
import { useAuthStore } from "../../stores/auth-store";
import { InviteParticipantModal, RevokeParticipantModal } from "./ParticipantModals";

interface Props {
  projectId: string;
}

// design-ref "Rozet etiketleri (D5 ek)" — Davetli / Aktif / Gönderdi / İptal.
const STATUS_LABELS: Record<ParticipantStatus, string> = {
  invited: "Davetli",
  active: "Aktif",
  submitted: "Gönderdi",
  revoked: "İptal",
};

// design-ref "Rozet renk semantiği (D5)" — Davetli=gri, Aktif=terra-cotta(primary),
// Gönderildi=yeşil(success), İptal=kırmızı(error). Mevcut semantic token'lar.
const STATUS_COLORS: Record<ParticipantStatus, string> = {
  invited: "var(--color-text-3)",
  active: "var(--color-primary)",
  submitted: "var(--color-success)",
  revoked: "var(--color-error)",
};

export function ParticipantsCard({ projectId }: Props): JSX.Element | null {
  const user = useAuthStore((s) => s.user);
  const canManage = user?.permissions["projects.invite_client"] === true;

  const [participants, setParticipants] = useState<ProjectParticipant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<ProjectParticipant | null>(null);

  const [rowLoading, setRowLoading] = useState<Record<string, boolean>>({});
  const [rowError, setRowError] = useState<Record<string, string>>({});
  // eng-review D9 — davet/resend yanıtı mailSent:false ise satırda uyarı rozeti.
  const [mailWarning, setMailWarning] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!canManage) return;
    let aborted = false;
    setLoading(true);
    listParticipants(projectId)
      .then((res) => {
        if (!aborted) {
          setParticipants(res.participants);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!aborted) setError(err instanceof Error ? err.message : "Katılımcılar yüklenemedi");
      })
      .finally(() => {
        if (!aborted) setLoading(false);
      });
    return () => {
      aborted = true;
    };
  }, [projectId, canManage, refreshTick]);

  if (!canManage) return null;

  async function handleSetStatus(
    p: ProjectParticipant,
    status: "active" | "revoked",
  ): Promise<void> {
    setRowLoading((s) => ({ ...s, [p.id]: true }));
    setRowError((s) => ({ ...s, [p.id]: "" }));
    try {
      await setParticipantStatus(projectId, p.id, status);
      if (status === "active") setMailWarning((s) => ({ ...s, [p.id]: false }));
      setRefreshTick((t) => t + 1);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "İşlem gerçekleştirilemedi";
      setRowError((s) => ({ ...s, [p.id]: msg }));
    } finally {
      setRowLoading((s) => ({ ...s, [p.id]: false }));
    }
  }

  async function handleResend(p: ProjectParticipant): Promise<void> {
    setRowLoading((s) => ({ ...s, [p.id]: true }));
    setRowError((s) => ({ ...s, [p.id]: "" }));
    try {
      const res = await resendParticipantInvite(projectId, p.id);
      setMailWarning((s) => ({ ...s, [p.id]: !res.mailSent }));
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Mail yeniden gönderilemedi";
      setRowError((s) => ({ ...s, [p.id]: msg }));
    } finally {
      setRowLoading((s) => ({ ...s, [p.id]: false }));
    }
  }

  return (
    <section className="mt-8">
      <div className="flex items-baseline justify-between mb-3">
        <h2
          className="text-xl"
          style={{ fontFamily: "var(--font-display)", color: "var(--color-text)" }}
        >
          Müşteri Katılımcıları
          {participants.length > 0 && (
            <span
              style={{
                marginLeft: 8,
                fontSize: "0.85rem",
                color: "var(--color-text-3)",
                fontWeight: 400,
              }}
            >
              ({participants.length.toString()})
            </span>
          )}
        </h2>
        {participants.length > 0 && (
          <button
            onClick={() => setInviteOpen(true)}
            className="px-3 py-1.5 rounded-lg text-sm font-semibold"
            style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
          >
            + Davet Et
          </button>
        )}
      </div>

      <div
        className="rounded-xl overflow-hidden"
        style={{ background: "var(--color-surface)", border: "1px solid var(--color-border)" }}
      >
        {loading ? (
          <div className="p-6 text-center text-sm" style={{ color: "var(--color-text-3)" }}>
            Yükleniyor…
          </div>
        ) : error ? (
          <div className="p-6 text-center text-sm" style={{ color: "var(--color-error)" }}>
            <p className="mb-2">{error}</p>
            <button
              onClick={() => setRefreshTick((t) => t + 1)}
              className="text-xs underline"
              style={{ color: "var(--color-primary)" }}
            >
              Tekrar dene
            </button>
          </div>
        ) : participants.length === 0 ? (
          // design-ref "Rozet etiketleri (D5 ek)" — kart boş durumu.
          <div className="p-8 text-center">
            <p className="mb-3 text-sm" style={{ color: "var(--color-text-3)" }}>
              Henüz katılımcı davet edilmedi
            </p>
            <button
              onClick={() => setInviteOpen(true)}
              className="px-4 py-2 rounded-lg text-sm font-semibold"
              style={{ background: "var(--color-primary)", color: "var(--color-surface)" }}
            >
              Davet Et
            </button>
          </div>
        ) : (
          participants.map((p, i) => {
            const canResend = p.status === "invited";
            const canReopen = p.status === "submitted" || p.status === "revoked";
            const canRevoke = p.status !== "revoked";
            const busy = rowLoading[p.id] === true;
            return (
              <div
                key={p.id}
                style={{
                  padding: "12px 16px",
                  borderTop: i === 0 ? "none" : "1px solid var(--color-border)",
                }}
              >
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div
                      style={{ color: "var(--color-text)", fontWeight: 500, fontSize: "0.9rem" }}
                    >
                      {p.fullName}
                    </div>
                    <div
                      style={{
                        color: "var(--color-text-3)",
                        fontSize: "0.75rem",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {p.email}
                    </div>
                  </div>
                  <span
                    style={{
                      padding: "2px 10px",
                      borderRadius: 999,
                      background: `color-mix(in srgb, ${STATUS_COLORS[p.status]} 12%, white)`,
                      color: STATUS_COLORS[p.status],
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {STATUS_LABELS[p.status]}
                  </span>
                  {mailWarning[p.id] === true && (
                    <span
                      style={{
                        fontSize: "0.7rem",
                        color: "var(--color-error)",
                        whiteSpace: "nowrap",
                      }}
                      title="Davet/aktivasyon maili gönderilemedi"
                    >
                      ⚠ mail gönderilemedi
                    </span>
                  )}
                  {p.submittedAt && (
                    <span
                      style={{
                        fontSize: "0.72rem",
                        color: "var(--color-text-3)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      Gönderim:{" "}
                      {new Date(p.submittedAt).toLocaleDateString("tr-TR", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  )}
                  <div className="flex gap-2 flex-wrap justify-end">
                    {canResend && (
                      <button
                        onClick={() => void handleResend(p)}
                        disabled={busy}
                        className="px-2.5 py-1 text-xs rounded disabled:opacity-50"
                        style={{
                          background: "transparent",
                          border: "1px solid var(--color-border)",
                          color: "var(--color-text-2)",
                        }}
                      >
                        Maili Yeniden Gönder
                      </button>
                    )}
                    {canReopen && (
                      <button
                        onClick={() => void handleSetStatus(p, "active")}
                        disabled={busy}
                        className="px-2.5 py-1 text-xs rounded disabled:opacity-50"
                        style={{
                          background: "transparent",
                          border: "1px solid var(--color-primary)",
                          color: "var(--color-primary)",
                        }}
                      >
                        Yeniden Aç
                      </button>
                    )}
                    {canRevoke && (
                      <button
                        onClick={() => setRevokeTarget(p)}
                        disabled={busy}
                        className="px-2.5 py-1 text-xs rounded disabled:opacity-50"
                        style={{
                          background: "transparent",
                          border: "1px solid var(--color-error)",
                          color: "var(--color-error)",
                        }}
                      >
                        İptal
                      </button>
                    )}
                  </div>
                </div>
                {rowError[p.id] && (
                  <div className="mt-1 text-xs" style={{ color: "var(--color-error)" }}>
                    {rowError[p.id]}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <InviteParticipantModal
        projectId={projectId}
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        onInvited={(result) => {
          setInviteOpen(false);
          setMailWarning((s) => ({ ...s, [result.participantId]: !result.mailSent }));
          setRefreshTick((t) => t + 1);
        }}
      />

      <RevokeParticipantModal
        open={revokeTarget !== null}
        participantName={revokeTarget?.fullName ?? ""}
        loading={revokeTarget !== null && rowLoading[revokeTarget.id] === true}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={() => {
          if (!revokeTarget) return;
          const target = revokeTarget;
          void handleSetStatus(target, "revoked").then(() => setRevokeTarget(null));
        }}
      />
    </section>
  );
}
